/**
 * heat-score.service.ts — Service de scoring lead-centric
 *
 * Le scoring est base sur les INTENTIONS du contact, pas sur des metriques de clics.
 * Le vrai scoring est fait par l'IA (Claude API via Edge Function).
 * Ce service fournit les utilitaires cote client.
 */

export type HeatLevel = "cold" | "warm" | "hot";

export interface HeatLabel {
  emoji: string;
  label: string;
  color: string;
  bgColor: string;
}

export function getHeatLabel(score: string): HeatLabel {
  switch (score) {
    case "hot":
      return {
        emoji: "\uD83D\uDD25",
        label: "Chaud",
        color: "text-red-500",
        bgColor: "bg-red-500/10",
      };
    case "warm":
      return {
        emoji: "\uD83D\uDFE1",
        label: "Tiède",
        color: "text-yellow-500",
        bgColor: "bg-yellow-500/10",
      };
    default:
      return {
        emoji: "\uD83E\uDD76",
        label: "Froid",
        color: "text-blue-400",
        bgColor: "bg-blue-400/10",
      };
  }
}

export function getContactRoleLabel(role: string): { label: string; shortLabel: string; color: string; bgColor: string } {
  switch (role) {
    case "acheteur":
      return { label: "Acheteur", shortLabel: "Achet.", color: "text-blue-500", bgColor: "bg-blue-500/10" };
    case "vendeur":
      return { label: "Vendeur", shortLabel: "Vend.", color: "text-green-500", bgColor: "bg-green-500/10" };
    case "bailleur":
      return { label: "Bailleur", shortLabel: "Bail.", color: "text-rose-500", bgColor: "bg-rose-500/10" };
    case "pro_mdb":
      return { label: "Pro MDB", shortLabel: "MDB", color: "text-orange-500", bgColor: "bg-orange-500/10" };
    case "pro_investisseur":
      return { label: "Pro Invest.", shortLabel: "Invest.", color: "text-orange-500", bgColor: "bg-orange-500/10" };
    case "locataire":
      return { label: "Locataire", shortLabel: "Locat.", color: "text-teal-500", bgColor: "bg-teal-500/10" };
    case "ambassadeur":
      return { label: "Ambassadeur", shortLabel: "Ambass.", color: "text-amber-500", bgColor: "bg-amber-500/10" };
    default:
      return { label: "Indefini", shortLabel: "?", color: "text-muted-foreground", bgColor: "bg-muted" };
  }
}

export function getLifecyclePhaseLabel(phase: string): { label: string; shortLabel: string; color: string; bgColor: string } {
  switch (phase) {
    case "prospect_froid":
      return { label: "Prospect froid", shortLabel: "Froid", color: "text-blue-400", bgColor: "bg-blue-400/10" };
    case "prospect_actif":
      return { label: "Prospect actif", shortLabel: "Actif", color: "text-yellow-500", bgColor: "bg-yellow-500/10" };
    case "prospect_dormant":
      return { label: "Dormant", shortLabel: "Dorm.", color: "text-muted-foreground", bgColor: "bg-muted" };
    case "en_transaction":
      return { label: "En transaction", shortLabel: "Transac.", color: "text-blue-500", bgColor: "bg-blue-500/10" };
    case "client":
      return { label: "Client", shortLabel: "Client", color: "text-green-500", bgColor: "bg-green-500/10" };
    case "ambassadeur":
      return { label: "Ambassadeur", shortLabel: "Ambass.", color: "text-amber-500", bgColor: "bg-amber-500/10" };
    case "archive":
      return { label: "Archive", shortLabel: "Arch.", color: "text-muted-foreground", bgColor: "bg-muted" };
    default:
      return { label: phase, shortLabel: phase, color: "text-muted-foreground", bgColor: "bg-muted" };
  }
}

/** @deprecated Plus affiche dans le UI — conserve pour l'export CSV et le backend */
export function getLeadSourceLabel(source: string): { label: string; color: string } {
  switch (source) {
    case "scrapped":
      return { label: "Sniper Web", color: "text-sky-500" };
    case "csv":
      return { label: "CSV", color: "text-blue-500" };
    case "terrain":
      return { label: "Terrain", color: "text-green-500" };
    case "vitrine":
      return { label: "Site Agent", color: "text-orange-500" };
    case "newsletter":
      return { label: "Newsletter", color: "text-pink-500" };
    case "crm":
      return { label: "CRM", color: "text-gray-500" };
    case "pro":
      return { label: "Pro", color: "text-emerald-500" };
    case "ambassadeur":
      return { label: "Ambassadeur", color: "text-amber-500" };
    default:
      return { label: source, color: "text-muted-foreground" };
  }
}

/** @deprecated Remplace par getContactRoleLabel + getLifecyclePhaseLabel dans le UI */
export function getEntryCampaignLabel(type: string): { label: string; shortLabel: string } {
  switch (type) {
    case "lead_pro":
      return { label: "Lead Pro", shortLabel: "Pro" };
    case "lead_scrappe":
      return { label: "Lead Scrappe", shortLabel: "Scrape" };
    case "lead_terrain":
      return { label: "Lead Terrain", shortLabel: "Terrain" };
    case "lead_vitrine":
      return { label: "Lead Site Agent", shortLabel: "Site Agent" };
    case "lead_crm_actif":
      return { label: "Lead CRM Actif", shortLabel: "CRM" };
    case "lead_crm_inconnu":
      return { label: "Origine inconnue", shortLabel: "Inconnue" };
    default:
      return { label: type, shortLabel: type };
  }
}
